import { ACDSessionManager } from '@nice-devone/core-sdk';
import { logger } from '../../../../utils/logger';
import { CXoneEventsSubscriptionContext, EventSubscription } from '../../subscription-context';
import { applyCallStateFromAgentState } from './agent-state-call-bridge';
import {
  applyAcwStateFromEvent,
  parseAgentStateEvent,
  parseBooleanFlag,
  resolveAgentReason,
} from './agent-state-parsing.util';
import { applyUnavailableCodes, mapUnavailableCodes } from './unavailable-codes.util';

export function subscribeAgentStateEvents(
  acdSession: ACDSessionManager,
  ctx: CXoneEventsSubscriptionContext
): EventSubscription | null {
  if (!acdSession.agentStateSubject) {
    return null;
  }

  return acdSession.agentStateSubject.subscribe((event: unknown) => {
    const eventObj = parseAgentStateEvent(event);
    if (!eventObj) {
      return;
    }

    logger.debug('[Events] Agent state event:', {
      state: eventObj.currentState?.state || eventObj.currentState?.cxoneState,
      reason: eventObj.currentState?.reason,
      acwTimeout: eventObj.currentState?.acwTimeout,
      IsAcw: eventObj.agentStateData?.IsAcw,
      AcwTimeout: eventObj.agentStateData?.AcwTimeout,
      CurrentOutReason: eventObj.agentStateData?.CurrentOutReason,
    });

    applyAcwStateFromEvent(eventObj, ctx);

    const stateStartTime = eventObj.currentState?.startTime
      ? new Date(eventObj.currentState.startTime)
      : new Date();

    const { reasonName, reasonCode } = resolveAgentReason(
      eventObj,
      ctx.unavailableCodesRef.current
    );

    const stateName =
      eventObj.currentState?.state || eventObj.currentState?.cxoneState || 'Unknown';

    const isAcwEvent =
      parseBooleanFlag(eventObj.agentStateData?.IsAcw ?? eventObj.agentStateData?.isAcw) ||
      parseBooleanFlag(
        typeof eventObj.currentState?.isACW === 'boolean'
          ? String(eventObj.currentState.isACW)
          : eventObj.currentState?.isACW
      );

    if (
      isAcwEvent ||
      stateName?.toLowerCase() === 'unavailable' ||
      stateName?.toLowerCase() === 'available'
    ) {
      logger.lifecycle('[ACW Flow] agent state event', {
        state: stateName,
        reason: reasonName,
        reasonCode,
        sdkIsInAcw: isAcwEvent,
        acwTimeout: eventObj.currentState?.acwTimeout ?? eventObj.agentStateData?.AcwTimeout,
      });
    }

    ctx.setAgentStateInternal({
      state: stateName,
      reason: reasonName,
      reasonCode,
      stateStartTime,
    });

    applyCallStateFromAgentState(stateName, eventObj, ctx);
  });
}

export function subscribeUnavailableCodeEvents(
  acdSession: ACDSessionManager,
  ctx: CXoneEventsSubscriptionContext
): EventSubscription | null {
  if (!acdSession.agentUpdateUnavailableCodeSubject) {
    return null;
  }

  return acdSession.agentUpdateUnavailableCodeSubject.subscribe((event: unknown) => {
    const mappedCodes = mapUnavailableCodes(event);
    if (!mappedCodes) {
      return;
    }
    applyUnavailableCodes(mappedCodes, ctx);
  });
}
